import React, { useEffect, useMemo, useState } from 'react';
import { useStyletron } from 'baseui';
import { Spinner } from 'baseui/spinner';
import { api } from '@gtintel/platform-api';
import { ProjectCreateContext, SubjectsContext } from '../ProjectApp/context';
import { useFlux } from '../state/useFlux';
import { uploadFileToPresignedUrl } from '../utils';
import { initialState, mutations } from './state';
const ProjectBuilder = ({
  children
}) => {
  const [state, mutate] = useFlux(initialState());
  const [css] = useStyletron();
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    api.project.getProjectBuilderInitialState().then(({
      data
    }) => {
      const newState = {
        templates: data.templates,
        subjectTypes: data.subjectTypes,
        jurisdictions: data.jurisdictions,
        subjects: [],
        project: {},
        selectedTemplate: null
      };
      mutate(mutations.init({
        state: newState
      }));
      setIsLoading(false);
    });
  }, [mutate]);
  const createProjectContext = useMemo(() => ({
    getID: () => state.project.id,
    getProject: () => state.project,
    create: async function () {
      const data = {
        ...state.project,
        subjects: state.subjects,
        template_id: state.selectedTemplate,
        instructions_html: state.project.instructions_html
      };
      const {
        data: project
      } = await api.project.createProjectV5(data);
      if (project) {
        mutate(mutations.create({
          project
        }));
      }
      return project;
    },
    edit: changes => {
      mutate(mutations.edit({
        changes
      }));
    },
    templates: () => state.templates,
    getTemplate: id => state.templates[id],
    subjects: () => state.subjects,
    jurisdictions: () => state.jurisdictions,
    getJurisdiction: id => state.jurisdictions.filter(jur => jur.id === id)[0],
    getInstructionsHtml: () => state.project.instructions_html,
    templateOptions: () => {
      const options = {};
      for (const template of Object.values(state.templates)) {
        const orgName = template.organization ? template.organization.name : 'GTI';
        if (!options[orgName]) {
          options[orgName] = [];
        }
        options[orgName].push({
          id: template.id,
          name: template.name
        });
      }
      return options;
    },
    getSelectedTemplate: () => state.selectedTemplate,
    getTurnAroundTime: () => state.project.turn_around_time
  }), [state, mutate]);
  const subjectsCtx = useMemo(() => ({
    list: () => state.subjects,
    edit: async function (id, changes) {
      mutate(mutations.subject.edit({
        id,
        changes
      }));
    },
    remove: async function (id) {
      mutate(mutations.subject.delete({
        id
      }));
    },
    reorder: async function (oldIndex, newIndex) {
      const subjects = [...state.subjects];
      const e = subjects.splice(oldIndex, 1)[0];
      subjects.splice(newIndex, 0, e);
      mutate(mutations.subject.reorder({
        subjects
      }));
    },
    addWithName: async function (name) {
      mutate(mutations.subject.add({
        name,
        id: String(state.subjects.length + 1),
        is_entity: false,
        jurisdictions: []
      }));
    },
    jurisdictionOptions: () => state.jurisdictions,
    jurisdictions: id => {
      const lookup = state.jurisdictions.reduce((memo, n) => {
        memo[n.id] = n;
        return memo;
      }, {});
      return state.subjects.find(s => String(s.id) === id).jurisdictions.map(i => lookup[i]);
    },
    subjectTypes: () => {
      return state.subjectTypes.reduce((memo, [key, value]) => {
        memo[key] = value;
        return memo;
      }, {});
    },
    listAttachments: id => state.subjects.find(subject => subject.id === id).attachments,
    addAttachment: async (subjectId, file, path) => {
      const resp = await uploadFileToPresignedUrl(file, subjectId, 'subject', path).catch(e => {
        console.error(e);
        return Promise.reject(e);
      });
      const result = await api.attachment.addToSubject(subjectId, {
        filename: file.name,
        path: resp.path,
        size: file.size
      });
      if (result.status === 201) {
        mutate(mutations.subject.addAttachment({
          id: subjectId,
          attachment: result.data
        }));
      }
    },
    removeAttachment: async (subjectId, attachmentId) => {
      const result = await api.attachment.delete(attachmentId);
      if (result.status === 204) {
        mutate(mutations.subject.removeAttachment({
          id: subjectId,
          attachmentId
        }));
      }
    }
  }), [state, mutate]);
  if (isLoading) return <section className={css({
    alignItems: 'center',
    display: 'flex',
    height: 'calc(100vh - 166px)',
    justifyContent: 'center',
    width: 'calc(100vw - 80px)'
  })}>
        <Spinner />
      </section>;
  return <ProjectCreateContext.Provider value={createProjectContext}>
      <SubjectsContext.Provider value={subjectsCtx}>
        {children}
      </SubjectsContext.Provider>
    </ProjectCreateContext.Provider>;
};
export default ProjectBuilder;